import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import type {
  GameBrandChangeReason,
  SetGameBrandsRequest,
  SetGameBrandsResult,
} from '@dayon/contracts';
import { api, ApiRequestError, isErrorCode } from '../../lib/api.ts';
import { noteError, noteIsValid, NOTE_MAX } from './reason-note.ts';
import {
  BRAND_REASONS,
  brandRefusalTitle,
  movedPairs,
  pairSentence,
  type BrandPair,
} from './game-brands.ts';

export interface PendingBrandChange {
  gameId: string;
  gameName: string;
  /** What the shelf held when the drawer opened. */
  before: BrandPair[];
  /** What the operator has set the chips to. */
  after: Map<string, boolean>;
}

/**
 * Confirm-and-say-why in front of a per-brand availability change.
 *
 * It names the pairs that MOVE rather than counting them: an operator cannot
 * check "3 brands" before committing to it, and this is the write that decides
 * which players can see a title at all.
 *
 * The whole set is sent, not the diff — the route decides what moved. What is
 * computed here is only what to SHOW, and the Save button stays disabled while
 * nothing has moved, so the route's "every brand already holds this setting"
 * refusal is a backstop rather than the first thing an operator meets.
 */
export function GameBrandsDialog({
  pending,
  holdsKey,
  mfa,
  onClose,
  onChanged,
}: {
  pending: PendingBrandChange | null;
  /**
   * HOLDS THE PERMISSION — not "may write". It used to be handed `canManage`,
   * which is the permission AND a two-factor-verified session folded into one
   * boolean, and it feeds the `allowed` branch below. So a key-holder on an
   * unverified session arrived here with `allowed: false` and was told "that
   * needs the games.manage permission", which they have. The next branch down
   * would have named the real wall.
   *
   * The two walls are separate questions and the copy already distinguishes
   * them, so the props have to as well.
   */
  holdsKey: boolean;
  /** Whether THIS session was verified with a second factor. */
  mfa: boolean;
  onClose: () => void;
  onChanged: (result: SetGameBrandsResult) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [reasonCode, setReasonCode] = useState<GameBrandChangeReason>('COMMERCIAL_DECISION');
  const [note, setNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Every opening starts clean: a reason typed for one title must never be
     waiting in the box for the next one. */
  useEffect(() => {
    if (!pending) return;
    setReasonCode('COMMERCIAL_DECISION');
    setNote('');
    setNoteTouched(false);
  }, [pending]);

  const moved = pending ? movedPairs(pending.before, pending.after) : [];

  const submit = useCallback(async () => {
    if (!pending || !noteIsValid(note)) return;
    setSubmitting(true);
    try {
      const body: SetGameBrandsRequest = {
        /* THE WHOLE SET, in the shape the contract asks for. The route works
           out which pairs moved; the client deciding that would let a stale
           render become a write. */
        brands: pending.before.map((b) => ({
          brandId: b.brandId,
          enabled: pending.after.get(b.brandId) ?? b.enabled,
        })),
        reasonCode,
        note: note.trim(),
      };
      const result = await api.put<SetGameBrandsResult>(
        `/api/games/${pending.gameId}/brands`,
        body,
      );
      toast(
        result.changed.length === 1
          ? `${result.name}: ${pairSentence({ brand: result.changed[0]!.brand, to: result.changed[0]!.to })}. The reason is in the shelf log.`
          : `${result.name}: ${result.changed.length} brands changed. The reason is in the shelf log.`,
      );
      onChanged(result);
      onClose();
    } catch (error: unknown) {
      const raw = error instanceof ApiRequestError ? error.code : undefined;
      const code = isErrorCode(raw) ? raw : undefined;
      showError({
        title: brandRefusalTitle(code, pending.gameName, { allowed: holdsKey, mfa }),
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. Try again shortly.',
        ...(raw ? { code: raw } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  }, [pending, note, reasonCode, holdsKey, mfa, onChanged, onClose, showError, toast]);

  return (
    <Dialog
      open={pending !== null}
      onClose={onClose}
      title={pending ? `Change which brands carry ${pending.gameName}?` : ''}
      dismissible={!submitting}
      actions={
        <>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={submitting}
            disabled={!noteIsValid(note) || moved.length === 0 || submitting}
            onClick={() => void submit()}
          >
            Change the shelf
          </Button>
        </>
      }
    >
      {pending && (
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="warning-circle" />
            <div>
              This decides who can SEE the title, not whether it works: a brand it is off for drops
              it from that brand&rsquo;s catalogue entirely. The game&rsquo;s own status and its
              provider&rsquo;s are untouched, and a title that is disabled or behind a provider that
              is not live stays unopenable wherever it is on.
            </div>
          </div>

          <div>
            <div className="panel-title">
              {moved.length === 1 ? 'One brand moves' : `${moved.length} brands move`}
            </div>
            <div className="row">
              {/* Named, not counted: "3 brands" is not something an operator
                  can check before committing to it. */}
              {moved.map((m) => (
                <Tag key={m.brand} tone={m.to ? 'accent' : 'quiet'}>
                  {pairSentence(m)}
                </Tag>
              ))}
            </div>
          </div>

          <Field
            label="Reason"
            hint="Three codes, not six: a provider incident or an RTP deviation is never true of one brand and false of another."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value as GameBrandChangeReason)}
              >
                {BRAND_REASONS.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            error={noteError(note, noteTouched)}
            hint="At least 10 characters. The code says what kind of thing happened; the note says what actually did."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => setNoteTouched(true)}
                placeholder="Bingo Barrio's licence does not cover live tables from this provider."
              />
            )}
          </Field>
        </div>
      )}
    </Dialog>
  );
}
